import * as THREE from 'three'
import { AtlasResult } from './pixelart'

const _camRight = new THREE.Vector3()
const _up = new THREE.Vector3(0, 1, 0)

/**
 * A single-draw-call batch of view-aligned pixel-art billboards. Each frame:
 * begin(camera) → add(...) per visible sprite → end(). Quads are CPU-billboarded
 * (upright, facing the camera horizontally) so sprites stand on the ground.
 */
export class SpriteBatch {
  readonly mesh: THREE.Mesh
  private pos: Float32Array
  private uvs: Float32Array
  private col: Float32Array
  private posAttr: THREE.BufferAttribute
  private uvAttr: THREE.BufferAttribute
  private colAttr: THREE.BufferAttribute
  private geom: THREE.BufferGeometry
  private atlas: AtlasResult
  private count = 0
  private readonly cap: number

  constructor(atlas: AtlasResult, capacity: number) {
    this.atlas = atlas
    this.cap = capacity
    this.pos = new Float32Array(capacity * 4 * 3)
    this.uvs = new Float32Array(capacity * 4 * 2)
    this.col = new Float32Array(capacity * 4 * 3)
    const index = new Uint32Array(capacity * 6)
    for (let i = 0; i < capacity; i++) {
      const v = i * 4
      const o = i * 6
      index[o] = v
      index[o + 1] = v + 1
      index[o + 2] = v + 2
      index[o + 3] = v
      index[o + 4] = v + 2
      index[o + 5] = v + 3
    }

    this.geom = new THREE.BufferGeometry()
    this.posAttr = new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage)
    this.uvAttr = new THREE.BufferAttribute(this.uvs, 2).setUsage(THREE.DynamicDrawUsage)
    this.colAttr = new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage)
    this.geom.setAttribute('position', this.posAttr)
    this.geom.setAttribute('uv', this.uvAttr)
    this.geom.setAttribute('color', this.colAttr)
    this.geom.setIndex(new THREE.BufferAttribute(index, 1))

    const mat = new THREE.MeshBasicMaterial({
      map: atlas.texture,
      vertexColors: true,
      transparent: true,
      alphaTest: 0.5,
      side: THREE.DoubleSide,
      depthWrite: true,
    })
    this.mesh = new THREE.Mesh(this.geom, mat)
    this.mesh.frustumCulled = false
  }

  begin(camera: THREE.Camera): void {
    this.count = 0
    // camera's world-space right axis, flattened to the ground plane
    _camRight.setFromMatrixColumn(camera.matrixWorld, 0)
    _camRight.y = 0
    if (_camRight.lengthSq() < 1e-6) _camRight.set(1, 0, 0)
    _camRight.normalize()
  }

  /** Queue one sprite. (x,y,z) is the feet/base position; size is world height. */
  add(name: string, x: number, y: number, z: number, size: number, r: number, g: number, b: number): void {
    if (this.count >= this.cap) return
    const rect = this.atlas.uv.get(name)
    if (!rect) return
    const i = this.count++
    const vp = i * 12
    const vu = i * 8
    const vc = i * 12

    const hw = size * 0.5
    const rx = _camRight.x * hw
    const rz = _camRight.z * hw
    // bottom-left, bottom-right, top-right, top-left
    // bl
    this.pos[vp] = x - rx
    this.pos[vp + 1] = y
    this.pos[vp + 2] = z - rz
    // br
    this.pos[vp + 3] = x + rx
    this.pos[vp + 4] = y
    this.pos[vp + 5] = z + rz
    // tr
    this.pos[vp + 6] = x + rx
    this.pos[vp + 7] = y + size
    this.pos[vp + 8] = z + rz
    // tl
    this.pos[vp + 9] = x - rx
    this.pos[vp + 10] = y + size
    this.pos[vp + 11] = z - rz

    // uv (flipY=false: v0=top row, v1=bottom row)
    this.uvs[vu] = rect.u0
    this.uvs[vu + 1] = rect.v1
    this.uvs[vu + 2] = rect.u1
    this.uvs[vu + 3] = rect.v1
    this.uvs[vu + 4] = rect.u1
    this.uvs[vu + 5] = rect.v0
    this.uvs[vu + 6] = rect.u0
    this.uvs[vu + 7] = rect.v0

    for (let k = 0; k < 4; k++) {
      this.col[vc + k * 3] = r
      this.col[vc + k * 3 + 1] = g
      this.col[vc + k * 3 + 2] = b
    }
  }

  end(): void {
    const n = this.count
    this.geom.setDrawRange(0, n * 6)
    this.posAttr.needsUpdate = true
    this.uvAttr.needsUpdate = true
    this.colAttr.needsUpdate = true
  }
}
